import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { notificationApi, type NotificationPreference } from '../services/api';

/**
 * Inställningar → Notiser (ClickUp 869fkva3u): the user chooses which
 * categories also come by e-mail. The notifications in the app always stay,
 * and account, payments and deadlines always go out by e-mail.
 */
export function NotificationSettings() {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<NotificationPreference[] | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    notificationApi.getPreferences()
      .then(setPreferences)
      .catch(() => {
        setPreferences([]);
        setMessage({ type: 'danger', text: t('settings.notifications.loadError') });
      });
  }, [t]);

  const toggle = async (preference: NotificationPreference) => {
    const email = !preference.email;
    setSaving(preference.category);
    setMessage(null);
    try {
      setPreferences(await notificationApi.updatePreference(preference.category, email));
      setMessage({ type: 'success', text: t('settings.notifications.saved') });
    } catch {
      setMessage({ type: 'danger', text: t('settings.notifications.saveError') });
    } finally {
      setSaving(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('settings.notifications.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('settings.notifications.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('settings.notifications.description')}</p>
        </div>
      </div>
      <Card>
        <div className="card-body">
          <p className="mb-4">{t('settings.notifications.intro')}</p>
          <div aria-live="polite">
            {message && <div className={`alert alert-${message.type}`} role="status">{message.text}</div>}
          </div>
          {preferences === null ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('settings.notifications.loading')}</span>
              </div>
            </div>
          ) : (
            <ul className="list-group notification-settings-list">
              {preferences.map(preference => {
                const id = `notification-email-${preference.category}`;
                const title = t(`settings.notifications.categories.${preference.category}.title`);
                return (
                  <li key={preference.category} className="list-group-item d-flex align-items-start gap-3 py-3">
                    <div className="flex-grow-1 min-w-0">
                      <label className="fw-semibold d-block" htmlFor={id}>{title}</label>
                      <p className="text-muted small mb-0">
                        {t(`settings.notifications.categories.${preference.category}.description`)}
                      </p>
                      {preference.emailRequired && (
                        <p className="small mb-0 mt-1">
                          <i className="feather-lock me-1" aria-hidden="true" />
                          {t('settings.notifications.required')}
                        </p>
                      )}
                    </div>
                    <div className="form-check form-switch notification-settings-switch flex-shrink-0 mb-0">
                      <input
                        id={id}
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={preference.email}
                        disabled={preference.emailRequired || saving !== null}
                        aria-describedby={`${id}-state`}
                        onChange={() => void toggle(preference)}
                      />
                      <span id={`${id}-state`} className="visually-hidden">
                        {t(preference.email ? 'settings.notifications.emailOn' : 'settings.notifications.emailOff')}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-muted small mt-3 mb-0">{t('settings.notifications.requiredHelp')}</p>
        </div>
      </Card>
    </DashboardLayout>
  );
}

export default NotificationSettings;
